import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { GROUP_MATCHES, KNOCKOUT_MATCHES, GROUPS, TEAMS, isGroupMatch, formatVenue, matchdayLabel } from '../lib/matches';
import { Flag } from '../lib/flags';
import WinnerCelebration from '../components/WinnerCelebration';
import { format, isPast } from 'date-fns';
import EventTimeline from '../components/EventTimeline';

const isKnownTeam = (name) => TEAMS.includes(name);

const onlyDigits = (v) => v.replace(/[^0-9]/g, '').slice(0, 2);
const roundShort = (r) => ({ R32: 'Rd 32', R16: 'Rd 16', QF: 'Quarter', SF: 'Semi', '3RD': '3rd Place', FINAL: 'Final', FRIENDLY: 'Friendly' }[r] || r);

// Knockout "actual result" line (extra time / penalties) — display only; scoring uses the 90' score.
function actualResultLine(res) {
  if (!res) return null;
  const parts = [];
  if (res.aetHome != null && res.aetAway != null) parts.push(`a.e.t. ${res.aetHome}–${res.aetAway}`);
  if (res.penHome != null && res.penAway != null) parts.push(`${res.penHome}–${res.penAway} on pens`);
  return parts.length ? parts.join(' · ') : null;
}

function timeUntil(ko) {
  if (!ko) return null;
  const diff = new Date(ko) - Date.now();
  if (diff <= 0 || diff > 48 * 3600000) return null;
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return { text: h > 0 ? `${h}h ${m}m` : `${m}m`, urgent: diff < 2 * 3600000 };
}

const M1_KO    = new Date('2026-06-11T19:00:00Z');
const GROUP_END = new Date('2026-06-28T04:00:00Z');

// Returns true when ESPN has gone quiet — last write was >4 mins ago (2+ missed invocations)
const isStale = (updatedAt) => updatedAt ? (Date.now() - new Date(updatedAt).getTime()) > 4 * 60 * 1000 : false;

// ── Points badge for admin coverage grid (shown after FT only) ───────────────
function CovOutcome({ p, result }) {
  if (!result || !p.hasPrediction) return null;
  const dir   = (h, a) => (h > a ? 'h' : a > h ? 'a' : 'd');
  const exact = p.homeScore === result.homeScore && p.awayScore === result.awayScore;
  const out   = dir(p.homeScore, p.awayScore) === dir(result.homeScore, result.awayScore);
  const base  = exact ? 2 : out ? 1 : 0;
  const s     = { fontSize: '0.65rem', borderRadius: '3px', padding: '1px 4px', fontFamily: 'var(--font-mono)', letterSpacing: '.04em', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '2px' };
  const x2    = p.joker && <span style={{ fontSize: '0.55rem', opacity: 0.85 }}>×2</span>;
  if (base === 2) return <span title="Exact score"    style={{ ...s, background: 'var(--volt)',   color: 'var(--on-volt)' }}>+{base}{x2}</span>;
  if (base === 1) return <span title="Correct result" style={{ ...s, background: 'var(--spark)',  color: 'var(--on-spark)' }}>+{base}{x2}</span>;
  return              <span title="Wrong result"      style={{ ...s, background: 'var(--danger)', color: '#fff' }}>+0{x2}</span>;
}

// ── Points pill ───────────────────────────────────────────────────────────────
function Points({ prediction, result }) {
  const outcome = (h, a) => (h > a ? 'h' : a > h ? 'a' : 'd');
  const exact = prediction.homeScore === result.homeScore && prediction.awayScore === result.awayScore;
  const out = outcome(prediction.homeScore, prediction.awayScore) === outcome(result.homeScore, result.awayScore);
  const base = exact ? 2 : out ? 1 : 0;
  const pts = prediction.joker ? base * 2 : base;
  const type = base === 2 ? 'exact' : base === 1 ? 'out' : 'miss';
  return (
    <span className={`pts ${type}`}>
      <span className="val">+{pts}</span>
      {prediction.joker && <span className="x2">2×</span>}
    </span>
  );
}

// ── Group league table ────────────────────────────────────────────────────────
function GroupTable({ groupMatches, resultsMap }) {
  const teams = [...new Set(groupMatches.flatMap(m => [m.home, m.away]))];
  const standings = teams.map(team => {
    let P = 0, W = 0, D = 0, L = 0, GF = 0, GA = 0;
    for (const m of groupMatches) {
      const r = resultsMap[m.id];
      if (!r || r.status !== 'FT') continue;
      const isHome = m.home === team, isAway = m.away === team;
      if (!isHome && !isAway) continue;
      P++;
      const tf = isHome ? r.homeScore : r.awayScore;
      const ta = isHome ? r.awayScore : r.homeScore;
      GF += tf; GA += ta;
      if (tf > ta) W++; else if (tf === ta) D++; else L++;
    }
    return { team, P, W, D, L, GF, GA, GD: GF - GA, Pts: W * 3 + D };
  });
  standings.sort((a, b) => b.Pts - a.Pts || b.GD - a.GD || b.GF - a.GF || a.team.localeCompare(b.team));
  if (!standings.some(s => s.P > 0)) return null;
  return (
    <div className="group-table">
      <table>
        <thead>
          <tr><th>Team</th><th title="Played">P</th><th title="Won">W</th><th title="Drawn">D</th><th title="Lost">L</th><th title="Goal difference">GD</th><th title="Points">Pts</th></tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <tr key={s.team} className={i < 2 ? 'qualify-auto' : i === 2 ? 'qualify-possible' : ''}>
              <td><Flag team={s.team} size="sm" /><span className="gt-name">{s.team}</span></td>
              <td>{s.P}</td><td>{s.W}</td><td>{s.D}</td><td>{s.L}</td>
              <td>{s.GD > 0 ? `+${s.GD}` : s.GD}</td>
              <td><b>{s.Pts}</b></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Compact match row (clickable) ─────────────────────────────────────────────
function MatchRow({ match, prediction, defaultPrediction, globalLocked, showDate, onClick, dim }) {
  const ko = new Date(match.ko);
  const known = match.home && isKnownTeam(match.home);
  const isFriendly = match.round === 'FRIENDLY';
  const locked = (known && isPast(ko)) || globalLocked;
  const status = match.result?.status;
  const isFt   = status === 'FT';
  const isLive = status === 'LIVE';
  const isHt   = status === 'HT';
  const hasLiveScore = isLive || isHt;
  const hasPrediction = prediction && prediction.homeScore !== undefined;
  // A frozen default is stored as a prediction row flagged fromDefault — it is the value that
  // was snapshotted at kick-off, so prefer it over the (current) defaultPrediction for display.
  const wasDefault = !!prediction?.fromDefault;
  const isManualPick = hasPrediction && !wasDefault;
  const defaultPick = wasDefault ? prediction : defaultPrediction;
  const cd = !locked && known ? timeUntil(match.ko) : null;

  let statusTag = null;
  if (isFt) {
    statusTag = <span className="tag ft">FT</span>;
  } else if (isHt) {
    statusTag = <span className="tag live">● HT</span>;
  } else if (isLive) {
    const min = !isStale(match.result?.updatedAt) ? match.result?.minute : null;
    statusTag = <span className="tag live">● {min ? `${min}` : 'Live'}</span>;
  } else if (known && isPast(ko)) {
    statusTag = <span className="tag live">● Live</span>;
  } else if (locked) {
    statusTag = <span className="tag lock">Locked</span>;
  } else if (isFriendly) {
    statusTag = <span className="tag" style={{ color: 'var(--text-faint)', borderColor: 'var(--line)' }}>Test</span>;
  }

  const rowCls = ['match-row'];
  if (isFt && hasPrediction) {
    const outcome = (h, a) => (h > a ? 'h' : a > h ? 'a' : 'd');
    const exact = prediction.homeScore === match.result.homeScore && prediction.awayScore === match.result.awayScore;
    const out = outcome(prediction.homeScore, prediction.awayScore) === outcome(match.result.homeScore, match.result.awayScore);
    if (exact) rowCls.push('match-row--exact');
    else if (out) rowCls.push('match-row--out');
    else rowCls.push('match-row--miss');
  } else if (!locked && hasPrediction) {
    if (prediction.joker) rowCls.push('match-row--joker');
    else rowCls.push('match-row--set');
  }

  return (
    <button className={rowCls.join(' ')} onClick={onClick} style={dim && isFt ? { opacity: 0.5 } : undefined}>
      <div className="match-row-head">
        <div className="match-row-meta">
          <span className="match-time">{known ? format(ko, showDate ? 'd MMM · HH:mm' : 'HH:mm') : 'TBD'}</span>
          <span className="match-group">{match.group ? `Group ${match.group}` : roundShort(match.round)}</span>
          {match.label && <span className="match-group" style={{ color: 'var(--text-faint)' }}>{match.label}</span>}
        </div>
        {statusTag}
      </div>

      <div className="match-row-teams">
        <div className="match-row-team">
          <Flag team={match.home} />
          <span className="match-row-name">{match.home || 'TBD'}</span>
        </div>
        <span className="match-row-vs">
          {(isFt || hasLiveScore) ? (
            <span className={`match-row-result-score${hasLiveScore ? ' live' : ''}`}>
              {match.result.homeScore}<span className="match-row-pred-dash">–</span>{match.result.awayScore}
            </span>
          ) : (!locked && hasPrediction) ? (
            <span className="match-row-pred-score">
              {prediction.homeScore}<span className="match-row-pred-dash">–</span>{prediction.awayScore}
            </span>
          ) : 'vs'}
        </span>
        <div className="match-row-team match-row-team--away">
          <span className="match-row-name">{match.away || 'TBD'}</span>
          <Flag team={match.away} />
        </div>
      </div>

      <div className="match-row-foot">
        {isManualPick ? (
          <>
            {isFt ? (
              <>
                <span className="match-row-pick">Pred: {prediction.homeScore}–{prediction.awayScore}</span>
                <Points prediction={prediction} result={match.result} />
              </>
            ) : (
              <>
                {prediction.joker && <span className="match-row-joker">2×</span>}
                <span className="match-row-pick" style={{ color: 'var(--text-faint)' }}>
                  {locked ? `Pred · ${prediction.homeScore}–${prediction.awayScore}` : 'Predicted'}
                </span>
              </>
            )}
          </>
        ) : locked ? (
          defaultPick ? (
            <>
              <span className="match-row-pick match-row-pick--none">Default · {defaultPick.homeScore}–{defaultPick.awayScore}</span>
              {isFt && !isFriendly && <Points prediction={defaultPick} result={match.result} />}
            </>
          ) : (
            <span className="match-row-pick match-row-pick--none">No prediction</span>
          )
        ) : (
          <span className="match-row-pick match-row-pick--none">Tap to predict</span>
        )}
        {hasLiveScore && <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-faint)', letterSpacing: '.04em' }}>↻ 1m</span>}
        {cd && <span className={`countdown${cd.urgent ? ' urgent' : ''}`}>{cd.text}</span>}
      </div>
    </button>
  );
}

// ── Match detail modal ────────────────────────────────────────────────────────
function MatchDetailModal({ match, prediction, onSave, onReset, onClose, jokersUsed, groupJokersUsed, saving, globalLocked, user, api, defaultPrediction, activeLeague }) {
  const ko = new Date(match.ko);
  const known = match.home && match.home !== 'TBD';
  const isFriendly = match.round === 'FRIENDLY';
  const locked = (known && isPast(ko)) || globalLocked;
  const hasResult = !!match.result;
  const hasPrediction = prediction && prediction.homeScore !== undefined;
  const isGroup = isGroupMatch(match.id);
  const wasJoker = !!prediction?.joker;
  // A frozen default is a prediction row flagged fromDefault — the value snapshotted at kick-off.
  // Prefer it over the (current) defaultPrediction so the result/live view shows what was scored.
  const wasDefault = !!prediction?.fromDefault;
  const isManualPick = hasPrediction && !wasDefault;
  const defaultPick = wasDefault ? prediction : defaultPrediction;

  const [homeScore, setHomeScore] = useState(prediction?.homeScore ?? '');
  const [awayScore, setAwayScore] = useState(prediction?.awayScore ?? '');
  const [joker, setJoker] = useState(prediction?.joker || false);
  const [coverageData, setCoverageData] = useState(null);
  const [showScores, setShowScores] = useState(false);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [otherPreds, setOtherPreds] = useState(null);
  const [otherPredsLoading, setOtherPredsLoading] = useState(false);

  const isLeagueAdmin = user?.groups?.includes('admin') || user?.groups?.includes('league-admin');

  const canAddJoker = !isFriendly && !joker && (
    (isGroup && jokersUsed < 4 && groupJokersUsed < 4) ||
    (!isGroup && jokersUsed < 4 && (jokersUsed - groupJokersUsed) < 2)
  );

  useEffect(() => {
    if (prediction) {
      setHomeScore(prediction.homeScore ?? '');
      setAwayScore(prediction.awayScore ?? '');
      setJoker(prediction.joker || false);
    }
  }, [prediction]);

  useEffect(() => {
    if (!isLeagueAdmin || !api) return;
    setCoverageLoading(true);
    api.adminGetMatchCoverage(match.id)
      .then(data => setCoverageData(data))
      .catch(() => {})
      .finally(() => setCoverageLoading(false));
  }, [match.id]); // eslint-disable-line

  useEffect(() => {
    if (!locked || isFriendly || !known || !api) return;
    setOtherPredsLoading(true);
    api.getMatchPredictions(match.id)
      .then(data => setOtherPreds(data.predictions ?? []))
      .catch(() => setOtherPreds([]))
      .finally(() => setOtherPredsLoading(false));
  }, [match.id]); // eslint-disable-line

  const handleShare = () => {
    const myPred = isManualPick
      ? `${match.home} ${prediction.homeScore}–${prediction.awayScore} ${match.away}`
      : defaultPick
        ? `${match.home} ${defaultPick.homeScore}–${defaultPick.awayScore} ${match.away} (default)`
        : `${match.home} vs ${match.away}`;
    const text = `My WC2026 prediction: ${myPred}`;
    const url = window.location.origin;
    if (navigator.share) {
      navigator.share({ title: 'WC2026 Predictor', text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
    }
  };

  const handleSave = async () => {
    const h = parseInt(homeScore, 10);
    const a = parseInt(awayScore, 10);
    if (isNaN(h) || isNaN(a)) return;
    await onSave(match.id, { homeScore: h, awayScore: a, joker: isFriendly ? false : joker });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const status     = match.result?.status;
  const isFt       = status === 'FT';
  const isLive     = status === 'LIVE';
  const isHt       = status === 'HT';
  const hasLiveScore = isLive || isHt;

  let centerState;
  if (!locked) centerState = 'open';
  else if (isFt) centerState = 'ft';
  else centerState = 'live';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">✕</button>

        {/* Meta */}
        <div className="modal-meta-row">
          <span className="match-group">{match.group ? `Group ${match.group}` : roundShort(match.round)}</span>
          {match.label && <span className="match-group" style={{ color: 'var(--text-faint)' }}>{match.label}</span>}
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>
            {known ? format(ko, 'EEE d MMM · HH:mm') : 'TBD'}
          </span>
          {!isFriendly && <span className="match-ref">{match.id}</span>}
        </div>
        {!isFriendly && formatVenue(match) && (
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)', letterSpacing: '.04em', marginTop: '-0.4rem', marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {formatVenue(match)}
          </div>
        )}

        {/* Teams */}
        <div className="modal-teams">
          <div className="modal-team modal-team--home">
            <span className="modal-team-name">{match.home || 'TBD'}</span>
            <Flag team={match.home} size="lg" />
          </div>
          <span className="modal-team-vs">vs</span>
          <div className="modal-team modal-team--away">
            <Flag team={match.away} size="lg" />
            <span className="modal-team-name">{match.away || 'TBD'}</span>
          </div>
        </div>

        {/* Open: score inputs */}
        {centerState === 'open' && (
          <div className="modal-pred-section">
            <span className="score-label pick" style={{ marginBottom: '1rem' }}>
              {hasPrediction ? (joker ? 'Your 2× pick — update below' : 'Your pick — update below') : 'Set your prediction'}
            </span>
            <div className="scoreboard" style={{ marginBottom: '1.25rem' }}>
              <input className="score-tile num" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
                value={homeScore} placeholder="–" aria-label={`${match.home} score`}
                onChange={(e) => setHomeScore(onlyDigits(e.target.value))} />
              <span className="score-dash">:</span>
              <input className="score-tile num" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={2}
                value={awayScore} placeholder="–" aria-label={`${match.away} score`}
                onChange={(e) => setAwayScore(onlyDigits(e.target.value))} />
            </div>
            <div className="modal-pred-actions">
              {!isFriendly && (joker || canAddJoker) && (
                <button className={`joker-btn${joker ? ' active' : ''}`} onClick={() => setJoker(j => !j)} title="Double your points">
                  <span className="diamond" /><span className="x2">{joker ? '2× On' : '2×'}</span>
                </button>
              )}
              <button className="btn btn-primary" onClick={handleSave}
                disabled={saving === match.id || homeScore === '' || awayScore === ''}>
                {saved ? 'Saved ✓' : saving === match.id ? '…' : hasPrediction ? 'Update' : 'Save'}
              </button>
              {onReset && prediction?.leagueOverride && (
                <button className="btn btn-ghost btn-sm" onClick={() => onReset(match.id)} title="Revert to global prediction">
                  Reset to global
                </button>
              )}
            </div>
            {activeLeague && (
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--volt)', marginTop: '0.5rem' }}>
                {prediction?.leagueOverride ? 'League-specific prediction' : 'Global prediction (no league override)'}
              </p>
            )}
            {!isFriendly && (
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-faint)', marginTop: '0.75rem' }}>
                {isFriendly ? 'Test match · no points' : 'Locks at kick-off'}
              </p>
            )}
          </div>
        )}

        {/* Locked / live: show live score (if available) then pick read-only */}
        {centerState === 'live' && (
          <div className="modal-pred-section">
            {hasLiveScore ? (
              <>
                <span className="score-label live" style={{ marginBottom: '0.75rem' }}>
                  <span className="dot" />
                  {isHt ? 'Half time' : `Live${!isStale(match.result.updatedAt) && match.result.minute ? ` · ${match.result.minute}` : ''}`}
                </span>
                <div className="score-final live" style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }}>
                  <span className="n">{match.result.homeScore}</span><span className="d">:</span><span className="n">{match.result.awayScore}</span>
                </div>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-faint)', textAlign: 'center', margin: '0 0 0.75rem', letterSpacing: '.06em', textTransform: 'uppercase' }}>{isStale(match.result.updatedAt) ? '⏳ Waiting for update…' : '↻ Updates every 1 min'}</p>
                {isManualPick && (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>
                    {wasJoker ? 'Your 2× pick' : 'Your pick'} · <b>{prediction.homeScore}–{prediction.awayScore}</b>
                    {wasJoker && <span style={{ color: 'var(--spark)' }}> · 2×</span>}
                  </div>
                )}
                {!isManualPick && defaultPick && (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>
                    Default · <b>{defaultPick.homeScore}–{defaultPick.awayScore}</b>
                  </div>
                )}
              </>
            ) : (
              <>
                {isManualPick ? (
                  <>
                    <span className="score-label live" style={{ marginBottom: '1rem' }}>
                      <span className="dot" />
                      {(wasJoker ? 'Your 2× pick' : 'Your pick') + ' · in play'}
                    </span>
                    <div className="score-final live" style={{ fontSize: '2.25rem' }}>
                      <span className="n">{prediction.homeScore}</span><span className="d">:</span><span className="n">{prediction.awayScore}</span>
                    </div>
                  </>
                ) : defaultPick ? (
                  <>
                    <span className="score-label default" style={{ marginBottom: '1rem' }}>
                      <span className="dot" />
                      Default · in play
                    </span>
                    <div className="score-final default" style={{ fontSize: '2.25rem' }}>
                      <span className="n">{defaultPick.homeScore}</span><span className="d">:</span><span className="n">{defaultPick.awayScore}</span>
                    </div>
                  </>
                ) : (
                  <span className="score-label live" style={{ marginBottom: '1rem' }}>
                    <span className="dot" />
                    In play · no prediction set
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* FT: result + pick + points */}
        {centerState === 'ft' && (
          <div className="modal-pred-section">
            <span className="score-label ft" style={{ marginBottom: '0.75rem' }}>
              {actualResultLine(match.result) ? 'Full time · 90 min (used for scoring)' : 'Full time'}
            </span>
            <div className="score-final" style={{ fontSize: '2.5rem', marginBottom: actualResultLine(match.result) ? '0.4rem' : '1rem' }}>
              <span className="n">{match.result.homeScore}</span><span className="d">:</span><span className="n">{match.result.awayScore}</span>
            </div>
            {actualResultLine(match.result) && (
              <div style={{ marginBottom: '1rem', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)', color: 'var(--text-muted)', textAlign: 'center' }}>
                {actualResultLine(match.result)}
                {match.result.winner && <> · <b style={{ color: 'var(--volt-bright)' }}>{match.result.winner}</b> advance</>}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              {isManualPick ? (
                <>
                  <span className="your-pick">
                    {wasJoker ? 'Your 2× pick' : 'Your pick'} · <b>{prediction.homeScore}–{prediction.awayScore}</b>
                    {wasJoker && <span style={{ color: 'var(--spark-bright)' }}> · 2×</span>}
                  </span>
                  <Points prediction={prediction} result={match.result} />
                </>
              ) : (
                <>
                  <span className="your-pick default">
                    Default score used{defaultPick && <> · <b>{defaultPick.homeScore}–{defaultPick.awayScore}</b></>}
                  </span>
                  {!isFriendly && defaultPick && <Points prediction={defaultPick} result={match.result} />}
                </>
              )}
            </div>
          </div>
        )}

        {/* Match event timeline (goals, cards, attendance, referee) — only after kick-off */}
        {locked && !isFriendly && (
          <EventTimeline matchId={match.id} live={hasLiveScore} />
        )}

        {/* Others' predictions (after kick-off, regular players) */}
        {locked && !isFriendly && !isLeagueAdmin && (
          <div className="modal-coverage">
            <div className="modal-coverage-head">
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--volt)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                {otherPredsLoading ? 'Loading…' : otherPreds ? `${otherPreds.length} prediction${otherPreds.length === 1 ? '' : 's'}` : 'Predictions'}
              </span>
              {otherPreds && otherPreds.length > 0 && (
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 'var(--t-xs)' }} onClick={() => setShowScores(s => !s)}>
                  {showScores ? 'Hide scores' : 'Show scores'}
                </button>
              )}
            </div>
            {otherPreds && otherPreds.length > 0 && (
              <div className="modal-cov-grid">
                {otherPreds.map((p, i) => (
                  <div key={i} className="modal-cov-player modal-cov-player--yes">
                    <span className="modal-cov-dot" />
                    <span className="modal-cov-name">{p.name}</span>
                    <span style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', marginLeft: 'auto' }}>
                      {showScores && <span className="modal-cov-score">{p.homeScore}–{p.awayScore}</span>}
                      {showScores && p.joker && !isFt && <span style={{ fontSize: '0.6rem', color: 'var(--spark)', fontFamily: 'var(--font-mono)' }}>2×</span>}
                      {!showScores && !isFt && <span className="modal-cov-check">✓</span>}
                      {isFt && <CovOutcome p={p} result={match.result} />}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {otherPreds && otherPreds.length === 0 && (
              <p style={{ fontSize: 'var(--t-xs)', color: 'var(--text-faint)', margin: '0.5rem 0 0' }}>No predictions set for this match.</p>
            )}
          </div>
        )}

        {/* Admin coverage */}
        {isLeagueAdmin && (
          <div className="modal-coverage">
            <div className="modal-coverage-head">
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--volt)', letterSpacing: '.1em', textTransform: 'uppercase' }}>
                {coverageLoading
                  ? 'Loading…'
                  : coverageData
                    ? `${coverageData.players.filter(p => p.hasPrediction).length}/${coverageData.players.length} predicted`
                    : 'Coverage'}
              </span>
              {coverageData && (
                <button className="btn btn-ghost btn-sm" style={{ fontSize: 'var(--t-xs)' }} onClick={() => setShowScores(s => !s)}>
                  {showScores ? 'Hide scores' : 'Show scores'}
                </button>
              )}
            </div>
            {coverageData && (
              <div className="modal-cov-grid">
                {coverageData.players.map(p => {
                  // Non-predictor who fell back to their default: keep the row red, but show
                  // their default score (prefixed "d") and the points it scored.
                  const usedDefault = !p.hasPrediction && p.usedDefault;
                  const covP = usedDefault
                    ? { hasPrediction: true, homeScore: p.defaultHomeScore, awayScore: p.defaultAwayScore, joker: false }
                    : p;
                  const scoreText = p.hasPrediction
                    ? `${p.homeScore}–${p.awayScore}`
                    : usedDefault ? `${p.defaultHomeScore}–${p.defaultAwayScore} (d)` : null;
                  return (
                  <div key={p.userId} className={`modal-cov-player ${p.hasPrediction ? 'modal-cov-player--yes' : 'modal-cov-player--no'}`}>
                    <span className="modal-cov-dot" />
                    <span className="modal-cov-name">{p.name}</span>
                    <span style={{ display: 'flex', gap: '0.25rem', alignItems: 'center', marginLeft: 'auto' }}>
                      {scoreText && showScores && (
                        <span className="modal-cov-score" title={usedDefault ? 'Default score used' : undefined}>{scoreText}</span>
                      )}
                      {p.hasPrediction && p.joker && showScores && !isFt && (
                        <span style={{ fontSize: '0.6rem', color: 'var(--spark)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>2×</span>
                      )}
                      {p.hasPrediction && !showScores && !hasResult && (
                        <span className="modal-cov-check">{p.joker ? '✓ 2×' : '✓'}</span>
                      )}
                      {hasResult && <CovOutcome p={covP} result={match.result} />}
                    </span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Share button */}
        {known && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '0.5rem' }}>
            <button className="btn btn-ghost btn-sm share-btn" onClick={handleShare}>
              <span className="share-icon">↗</span> Share prediction
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Schedule grouped by day ───────────────────────────────────────────────────
function ScheduleView({ matches, rowProps, recentCompleted }) {
  const upcomingRef = useRef(null);
  const [showEarlier, setShowEarlier] = useState(false);

  // Scroll to the first upcoming/active day on mount
  useEffect(() => {
    const id = setTimeout(() => {
      upcomingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => clearTimeout(id);
  }, []); // eslint-disable-line

  const days = {};
  for (const m of matches) {
    if (!m.ko) continue;
    const key = format(new Date(m.ko), 'yyyy-MM-dd');
    (days[key] || (days[key] = [])).push(m);
  }
  const sortedDays = Object.keys(days).sort();
  if (!sortedDays.length) return (
    <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No matches to show.</div>
  );

  const now = Date.now();
  const yesterday = format(new Date(now - 86400000), 'yyyy-MM-dd');

  // Split: days before yesterday are hidden behind the expand button
  const earlierDays  = sortedDays.filter(key => key < yesterday);
  const visibleDays  = sortedDays.filter(key => key >= yesterday);
  const earlierCount = earlierDays.reduce((sum, key) => sum + days[key].length, 0);

  // First visible day with an upcoming or live match
  const firstActiveKey = visibleDays.find(key =>
    days[key].some(m => new Date(m.ko) > now || m.result?.status === 'LIVE' || m.result?.status === 'HT')
  ) || visibleDays[visibleDays.length - 1] || sortedDays[sortedDays.length - 1];

  const renderDay = (key, ref) => {
    const dayMatches = days[key].sort((a, b) => new Date(a.ko) - new Date(b.ko));
    const d = new Date(key + 'T12:00:00Z');
    return (
      <div className="day" key={key} ref={ref || null}>
        <div className="day-head">
          <span className="dow">{format(d, 'EEEE')}</span>
          <span className="date">{format(d, 'd MMM')}</span>
          <span className="count">{dayMatches.length} {dayMatches.length === 1 ? 'match' : 'matches'}</span>
          <span className="rule" />
        </div>
        <div className="day-stack">
          {dayMatches.map(m => (
            <MatchRow key={m.id} match={m} prediction={rowProps.getPrediction(m.id)} defaultPrediction={rowProps.defaultPrediction} globalLocked={rowProps.globalLocked} dim={m.result?.status === 'FT'} onClick={() => rowProps.onOpen(m)} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Last 2 completed matches — always visible, greyed, quick reference */}
      {recentCompleted?.length > 0 && (
        <div style={{ opacity: 0.5, marginBottom: '1rem' }}>
          <div className="day-head">
            <span className="dow" style={{ fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', letterSpacing: '.08em', textTransform: 'uppercase' }}>Recent results</span>
            <span className="rule" />
          </div>
          <div className="day-stack">
            {recentCompleted.map(m => (
              <MatchRow key={m.id} match={m} prediction={rowProps.getPrediction(m.id)} defaultPrediction={rowProps.defaultPrediction} globalLocked={rowProps.globalLocked} showDate onClick={() => rowProps.onOpen(m)} />
            ))}
          </div>
        </div>
      )}

      {/* Expand button for earlier matches */}
      {earlierCount > 0 && !showEarlier && (
        <button className="show-earlier-btn" onClick={() => setShowEarlier(true)}>
          ↑ Show {earlierCount} earlier match{earlierCount === 1 ? '' : 'es'}
        </button>
      )}

      {/* Earlier days — rendered inline above yesterday when expanded */}
      {showEarlier && earlierDays.map(key => renderDay(key))}

      {/* Yesterday onwards — always visible */}
      {visibleDays.map(key => renderDay(key, key === firstActiveKey ? upcomingRef : null))}
    </>
  );
}

// ── Schedule grouped by matchday ──────────────────────────────────────────────
function currentMatchday(matches, overrides) {
  const enriched = matches.map(m => ({ ...m, matchday: overrides[m.id] ?? m.matchday ?? 1 }));
  const now = Date.now();
  // First matchday that has a future match — or last matchday if all done
  const upcoming = enriched.filter(m => new Date(m.ko) > now);
  if (!upcoming.length) return Math.max(...enriched.map(m => m.matchday));
  return Math.min(...upcoming.map(m => m.matchday));
}

function MatchdayView({ matches, rowProps, overrides, activeMatchday, onMatchdayChange }) {
  const enriched = matches.map(m => ({ ...m, matchday: overrides[m.id] ?? m.matchday ?? 1 }));
  const allMatchdays = [...new Set(enriched.map(m => m.matchday))].sort((a, b) => a - b);
  const effectiveMatchday = allMatchdays.includes(activeMatchday)
    ? activeMatchday
    : (allMatchdays[0] ?? activeMatchday);
  const dayMatches = enriched
    .filter(m => m.matchday === effectiveMatchday)
    .sort((a, b) => new Date(a.ko) - new Date(b.ko));

  const activeChipRef = useRef(null);
  useEffect(() => {
    // Align the active MD chip to the left of the strip — earlier matchdays
    // scroll off-screen to the left, later ones stay visible to the right.
    // This matches the "current + upcoming" focus most users want.
    activeChipRef.current?.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'smooth' });
  }, [effectiveMatchday]);

  return (
    <>
      <div className="chip-strip" style={{ marginBottom: '1rem' }}>
        {allMatchdays.map(md => (
          <button
            key={md}
            ref={effectiveMatchday === md ? activeChipRef : undefined}
            className={`chip${effectiveMatchday === md ? ' active' : ''}`}
            onClick={() => onMatchdayChange(md)}
          >
            {matchdayLabel(md)}
          </button>
        ))}
      </div>
      {dayMatches.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No matches to show.</div>
      ) : (
        <div className="day-stack">
          {dayMatches.map(m => (
            <MatchRow key={m.id} match={m} prediction={rowProps.getPrediction(m.id)} defaultPrediction={rowProps.defaultPrediction} globalLocked={rowProps.globalLocked} showDate dim onClick={() => rowProps.onOpen(m)} />
          ))}
        </div>
      )}
    </>
  );
}

// ── Knockout bracket ────────────────────────────────────────────────────────
// Classic two-sided tree: the left half (semi-final M101 and everything feeding it)
// grows leftward from the centre; the right half (M102) grows rightward; the Final
// (M104) and 3rd-place play-off (M103) sit in the middle. Structure is derived from
// the "Winner Mxx" feeder references in the fixture data, so it stays correct if the
// fixtures change.
const KO_BY_ID = Object.fromEntries(KNOCKOUT_MATCHES.map((m) => [m.id, m]));
function feedersOf(id) {
  const m = KO_BY_ID[id];
  if (!m) return [];
  const refs = [m.home, m.away].map((s) => {
    const r = /\bM(\d+)\b/.exec(s || '');
    return r ? `M${r[1]}` : null;
  });
  return refs.every((r) => r && KO_BY_ID[r]) ? refs : [];
}

function BracketCell({ id, resolve, getPrediction, onOpen, final }) {
  const m = resolve(id);
  const res = m.result;
  const pred = getPrediction(id);
  const live = res && (res.status === 'LIVE' || res.status === 'HT');
  const ft = res && res.status === 'FT';

  // Winner highlight prefers the recorded actual winner (ET/pens), else the 90' score.
  const teamRow = (name, score, isHome) => {
    const known = isKnownTeam(name);
    const won = ft && (res.winner
      ? res.winner === name
      : (isHome ? res.homeScore > res.awayScore : res.awayScore > res.homeScore));
    return (
      <div className={`bcell-row${won ? ' bcell-row--win' : ''}`}>
        {known ? <Flag team={name} size="sm" /> : <span className="bcell-flag-ph" />}
        <span className="bcell-team">{name}</span>
        <span className="bcell-score">{score != null ? score : ''}</span>
      </div>
    );
  };

  const extra = ft ? actualResultLine(res) : null;
  return (
    <button
      className={`bcell${final ? ' bcell--final' : ''}${live ? ' bcell--live' : ''}${pred ? ' bcell--pred' : ''}`}
      onClick={() => onOpen(m)}
    >
      {teamRow(m.home, res ? res.homeScore : null, true)}
      {teamRow(m.away, res ? res.awayScore : null, false)}
      <div className="bcell-foot">
        <span>{extra || format(new Date(m.ko), 'd MMM · HH:mm')}</span>
        {pred && <span className="bcell-pred-dot" title="You've predicted this">●</span>}
      </div>
    </button>
  );
}

function BracketTie({ id, side, cellProps }) {
  const kids = feedersOf(id);
  const self = <div className="bt-self"><BracketCell id={id} {...cellProps} /></div>;
  if (!kids.length) return <div className="bt bt--leaf">{self}</div>;
  const kidsEl = (
    <div className="bt-kids">
      {kids.map((k) => <BracketTie key={k} id={k} side={side} cellProps={cellProps} />)}
    </div>
  );
  return (
    <div className={`bt bt--${side}`}>
      {side === 'left' ? <>{kidsEl}{self}</> : <>{self}{kidsEl}</>}
    </div>
  );
}

function KnockoutBracket({ cellProps }) {
  return (
    <div className="bracket-scroll">
      <div className="bracket">
        <BracketTie id="M101" side="left" cellProps={cellProps} />
        <div className="bracket-center">
          <div className="bracket-center-label">Final</div>
          <BracketCell id="M104" {...cellProps} final />
          <div className="bracket-third">
            <div className="bracket-center-label">3rd place</div>
            <BracketCell id="M103" {...cellProps} />
          </div>
        </div>
        <BracketTie id="M102" side="right" cellProps={cellProps} />
      </div>
    </div>
  );
}

// Mobile knockout layout: round tabs, and for the selected round a vertically-scrolling
// two-column tree slice — that round's ties (left) connected to the next round's nodes (right),
// keeping the bracket/connector style but fitting a phone. Reuses BracketCell throughout.
const KO_ROUND_TABS = [
  { key: 'R32',   label: 'Round of 32',    next: 'R16' },
  { key: 'R16',   label: 'Round of 16',    next: 'QF' },
  { key: 'QF',    label: 'Quarter-finals', next: 'SF' },
  { key: 'SF',    label: 'Semi-finals',    next: 'FINAL' },
  { key: 'FINAL', label: 'Final',          next: null },
];
function KnockoutMobile({ cellProps }) {
  const [round, setRound] = useState('R32');
  const tab = KO_ROUND_TABS.find(t => t.key === round) || KO_ROUND_TABS[0];

  return (
    <div className="ko-m">
      <div className="chip-strip ko-m-tabs">
        {KO_ROUND_TABS.map(t => (
          <button key={t.key} className={`chip${round === t.key ? ' active' : ''}`} onClick={() => setRound(t.key)}>{t.label}</button>
        ))}
      </div>

      {tab.next === null ? (
        // Final + 3rd-place play-off
        <div className="ko-m-final">
          <BracketCell id="M104" {...cellProps} final />
          <div className="ko-m-3rd-label">3rd place play-off</div>
          <BracketCell id="M103" {...cellProps} />
        </div>
      ) : (
        // Each next-round node with the two ties (this round) that feed it.
        <div className="ko-m-pairs">
          {KNOCKOUT_MATCHES.filter(m => m.round === tab.next).map(nextM => {
            const kids = feedersOf(nextM.id);
            return (
              <div key={nextM.id} className="ko-m-pair">
                <div className="ko-m-feeders">
                  {kids.map(k => <BracketCell key={k} id={k} {...cellProps} />)}
                </div>
                <div className="ko-m-connector" aria-hidden="true" />
                <div className="ko-m-next">
                  <BracketCell id={nextM.id} {...cellProps} final={tab.next === 'FINAL'} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// True when the viewport is at/below the given width — used to switch knockout layouts.
function useMaxWidth(px) {
  const query = `(max-width:${px}px)`;
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// ── Page ──────────────────────────────────────────────────────────────────────
// Set to true to re-enable the Friendlies view tab. Hidden during the
// real tournament — the route/handlers/state still work, just no tab.
const SHOW_FRIENDLIES_VIEW = false;

const ALL_VIEWS = [
  { key: 'SCHEDULE',   label: 'Schedule'   },
  { key: 'GROUPS',     label: 'Groups'     },
  { key: 'KNOCKOUT',   label: 'Knockout'   },
  { key: 'FRIENDLIES', label: 'Friendlies' },
];
const VIEWS = SHOW_FRIENDLIES_VIEW
  ? ALL_VIEWS
  : ALL_VIEWS.filter(v => v.key !== 'FRIENDLIES');

export default function Matches() {
  const { user } = useAuth();
  const api = useApi();

  const [view, setView] = useState('SCHEDULE');
  const [scheduleSubview, setScheduleSubview] = useState(() => localStorage.getItem('wc2026_schedule_subview') || 'MATCHDAY');
  const [activeGroup, setActiveGroup] = useState('A');
  const [activeMatchday, setActiveMatchday] = useState(1);
  const [predictions, setPredictions] = useState([]);
  const [defaultPrediction, setDefaultPrediction] = useState(null);
  const [resultsMap, setResultsMap] = useState({});
  const [friendlies, setFriendlies] = useState([]);
  const [knockoutTeamOverrides, setKnockoutTeamOverrides] = useState({});
  const [matchdayOverrides, setMatchdayOverrides] = useState({});
  const [saving, setSaving] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [globalLocked, setGlobalLocked] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const initialLoaded = useRef(false);
  const autoOpenMatchId = useRef(new URLSearchParams(window.location.search).get('live'));
  const navigate = useNavigate();
  const knockoutMobile = useMaxWidth(900);

  useEffect(() => {
    Promise.all([
      api.getPredictions(user.sub).catch(() => []),
      api.getLockStatus().catch(() => ({ locked: false })),
      api.getResults().catch(() => []),
      api.getFriendlies().catch(() => []),
      api.getKnockoutTeams().catch(() => []),
      api.getMatchdayOverrides().catch(() => ({ overrides: {} })),
    ]).then(([preds, lockStatus, results, fris, koTeams, mdData]) => {
      setDefaultPrediction(preds.find(p => p.sk === 'DEFAULT') || null);
      setPredictions(preds.filter((p) => p.sk?.startsWith('PRED#')));
      setGlobalLocked(lockStatus.locked);
      setResultsMap(Object.fromEntries(results.map(r => [r.matchId, r])));
      setFriendlies(fris);
      setKnockoutTeamOverrides(Object.fromEntries(koTeams.map(t => [t.matchId, t])));
      const overrides = mdData?.overrides ?? {};
      setMatchdayOverrides(overrides);
      setActiveMatchday(currentMatchday([...GROUP_MATCHES, ...KNOCKOUT_MATCHES], overrides));
      setLoading(false);
      initialLoaded.current = true;
    }).catch((err) => { setMsg(`Load error: ${err.message}`); setLoading(false); });
  }, []); // eslint-disable-line

  // Auto-open match modal from ?live=M8 URL param
  useEffect(() => {
    if (loading || !autoOpenMatchId.current) return;
    const matchId = autoOpenMatchId.current;
    autoOpenMatchId.current = null;
    const url = new URL(window.location);
    url.searchParams.delete('live');
    window.history.replaceState({}, '', url);
    const match = [...GROUP_MATCHES, ...KNOCKOUT_MATCHES].find(m => m.id === matchId);
    if (match) setSelectedMatch(enrich(match));
  }, [loading]); // eslint-disable-line

  // Poll results every 30s while any match is LIVE or HT
  useEffect(() => {
    const hasLive = Object.values(resultsMap).some(r => r.status === 'LIVE' || r.status === 'HT');
    if (!hasLive) return;
    const id = setInterval(() => {
      api.getResults().catch(() => []).then(results =>
        setResultsMap(Object.fromEntries(results.map(r => [r.matchId, r])))
      );
    }, 30000);
    return () => clearInterval(id);
  }, [resultsMap]); // eslint-disable-line

  const getPrediction = useCallback((matchId) => predictions.find((p) => p.matchId === matchId), [predictions]);
  const jokersUsed = predictions.filter((p) => p.joker).length;
  const groupJokersUsed = predictions.filter((p) => p.joker && parseInt(p.matchId?.replace('M', ''), 10) <= 72).length;

  const handleSave = async (matchId, body) => {
    setSaving(matchId); setMsg('');
    try {
      await api.savePrediction(user.sub, matchId, body);
      setPredictions((prev) => [...prev.filter((p) => p.matchId !== matchId), {
        matchId, sk: `PRED#${matchId}`, ...body,
      }]);
      setMsg('Saved ✓');
      setTimeout(() => setMsg(''), 2000);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setSaving('');
    }
  };

  const enrich = (m) => ({ ...m, result: resultsMap[m.id] || null });
  const enrichKnockout = (m) => {
    const override = knockoutTeamOverrides[m.id];
    return enrich(override ? { ...m, home: override.home, away: override.away } : m);
  };

  const now = new Date();
  const inGroupStage = now >= M1_KO && now < GROUP_END;
  const groupJokersNeeded = Math.max(0, 2 - groupJokersUsed);
  const remainingGroupPredictable = GROUP_MATCHES.filter(m => !isPast(new Date(m.ko)) && !globalLocked).length;

  // Last 2 completed group matches for the Calendar "Recent results" section
  const recentCompleted = GROUP_MATCHES
    .filter(m => resultsMap[m.id]?.status === 'FT')
    .sort((a, b) => new Date(b.ko) - new Date(a.ko))
    .slice(0, 2)
    .reverse()
    .map(enrich);

  const rowProps = { getPrediction, globalLocked, defaultPrediction, onOpen: (m) => setSelectedMatch(enrich(m)) };
  const bracketCellProps = {
    resolve: (id) => enrichKnockout(KO_BY_ID[id]),
    getPrediction,
    onOpen: (m) => setSelectedMatch(m),
  };

  // Tournament winner = the team that won the Final (M104) on the 90-min result.
  const finalMatch = enrichKnockout(KO_BY_ID['M104']);
  const finalRes = finalMatch.result;
  // Prefer the recorded actual winner (covers finals decided by ET/pens); fall back to the 90' score.
  const tournamentWinner = finalRes && finalRes.status === 'FT'
    ? (finalRes.winner
        || (finalRes.homeScore !== finalRes.awayScore
            ? (finalRes.homeScore > finalRes.awayScore ? finalMatch.home : finalMatch.away)
            : null))
    : null;

  // Auto-play the celebration once per user when the Final result first lands.
  useEffect(() => {
    if (tournamentWinner && !localStorage.getItem('wc2026_celebrated_tournament')) {
      setCelebrate(true);
      localStorage.setItem('wc2026_celebrated_tournament', '1');
    }
  }, [tournamentWinner]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="kicker" style={{ marginBottom: 6 }}>World Cup 2026</div>
          <h1 className="page-title">Matches</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>
            Set your predictions before each match kicks off
            {msg && <span style={{ marginLeft: '1rem', color: msg.startsWith('Error') ? 'var(--danger)' : 'var(--good)' }}>{msg}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--s2)' }}>
          <div className="seg">
            {VIEWS.map((v) => (
              <button key={v.key} className={view === v.key ? 'active' : ''} onClick={() => setView(v.key)}>{v.label}</button>
            ))}
          </div>
        </div>
      </div>

      {globalLocked && (
        <div style={{ background: 'var(--danger-dim)', border: '1px solid var(--danger-line)', borderRadius: 'var(--r-sm)', padding: '.6rem 1rem', marginBottom: '1rem', color: 'var(--danger)', fontSize: 'var(--t-sm)', fontWeight: 600 }}>
          Predictions are currently locked by admin. Input is read-only.
        </div>
      )}

      {inGroupStage && groupJokersNeeded > 0 && remainingGroupPredictable > 0 && (
        <div className="joker-warning">
          <span>♦ {groupJokersNeeded} group joker{groupJokersNeeded > 1 ? 's' : ''} still to use</span>
          <span className="jw-dim">· {remainingGroupPredictable} group match{remainingGroupPredictable > 1 ? 'es' : ''} remaining to predict</span>
        </div>
      )}

      {(() => {
        const liveResults = Object.values(resultsMap).filter(r => r.status === 'LIVE' || r.status === 'HT');
        if (!liveResults.length) return null;
        const single = liveResults.length === 1 ? liveResults[0] : null;
        const match = single ? [...GROUP_MATCHES, ...KNOCKOUT_MATCHES].find(m => m.id === single.matchId) : null;
        const label = single && match
          ? `${match.home} ${single.homeScore}–${single.awayScore} ${match.away}${single.minute ? ` · ${single.minute}` : ''}`
          : `${liveResults.length} matches live now`;
        return (
          <button className="live-banner" onClick={() => {
            if (single && match) setSelectedMatch(enrich(match));
          }}>
            <span className="live-banner-dot" />
            <span className="live-banner-label">● {label}</span>
            <span className="live-banner-arrow">›</span>
          </button>
        );
      })()}

      {view === 'SCHEDULE' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button className={`chip${scheduleSubview === 'CALENDAR' ? ' active' : ''}`} onClick={() => { setScheduleSubview('CALENDAR'); localStorage.setItem('wc2026_schedule_subview', 'CALENDAR'); }}>Calendar</button>
            <button className={`chip${scheduleSubview === 'MATCHDAY' ? ' active' : ''}`} onClick={() => { setScheduleSubview('MATCHDAY'); localStorage.setItem('wc2026_schedule_subview', 'MATCHDAY'); }}>Matchday</button>
          </div>
          {scheduleSubview === 'CALENDAR' ? (
            <ScheduleView matches={[...GROUP_MATCHES.map(enrich), ...KNOCKOUT_MATCHES.map(enrichKnockout)]} rowProps={rowProps} recentCompleted={recentCompleted} />
          ) : (
            <MatchdayView
              matches={[...GROUP_MATCHES.map(enrich), ...KNOCKOUT_MATCHES.map(enrichKnockout)]}
              rowProps={rowProps}
              overrides={matchdayOverrides}
              activeMatchday={activeMatchday}
              onMatchdayChange={setActiveMatchday}
            />
          )}
        </>
      )}

      {view === 'GROUPS' && (
        <>
          <div className="chip-strip">
            {GROUPS.map((g) => (
              <button key={g} className={`chip ${activeGroup === g ? 'active' : ''}`} onClick={() => setActiveGroup(g)}>Group {g}</button>
            ))}
          </div>
          <GroupTable
            groupMatches={GROUP_MATCHES.filter(m => m.group === activeGroup)}
            resultsMap={resultsMap}
          />
          <div className="day-stack" style={{ marginTop: '0.75rem' }}>
            {GROUP_MATCHES.filter(m => m.group === activeGroup).map(m => (
              <MatchRow key={m.id} match={enrich(m)} prediction={getPrediction(m.id)} defaultPrediction={defaultPrediction} globalLocked={globalLocked} showDate dim={resultsMap[m.id]?.status === 'FT'} onClick={() => setSelectedMatch(enrich(m))} />
            ))}
          </div>
        </>
      )}

      {view === 'KNOCKOUT' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem' }}>
            <p className="page-subtitle" style={{ margin: 0 }}>
              Tap any tie to set your prediction. Teams fill in as each round completes{knockoutMobile ? '.' : ' — scroll sideways to see the full bracket.'}
            </p>
            {tournamentWinner && (
              <button className="btn btn-sm" style={{ flex: '0 0 auto' }} onClick={() => setCelebrate(true)}>🏆 Replay</button>
            )}
          </div>
          {knockoutMobile
            ? <KnockoutMobile cellProps={bracketCellProps} />
            : <KnockoutBracket cellProps={bracketCellProps} />}
        </>
      )}

      {celebrate && tournamentWinner && (
        <WinnerCelebration kind="tournament" name={tournamentWinner} team={tournamentWinner} onClose={() => setCelebrate(false)} />
      )}

      {view === 'FRIENDLIES' && (
        <>
          {friendlies.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
              No test matches configured yet.<br />
              An admin can add them from the Admin page.
            </div>
          ) : (
            <div className="day-stack">
              {friendlies.sort((a, b) => new Date(a.ko) - new Date(b.ko)).map(f => (
                <MatchRow key={f.id} match={enrich({ ...f, round: 'FRIENDLY' })} prediction={getPrediction(f.id)} defaultPrediction={null} globalLocked={globalLocked} onClick={() => setSelectedMatch(enrich({ ...f, round: 'FRIENDLY' }))} />
              ))}
            </div>
          )}
        </>
      )}

      {selectedMatch && (
        <MatchDetailModal
          match={{ ...selectedMatch, result: resultsMap[selectedMatch.id] || selectedMatch.result || null }}
          prediction={getPrediction(selectedMatch.id)}
          defaultPrediction={defaultPrediction}
          onSave={handleSave}
          onReset={null}
          onClose={() => setSelectedMatch(null)}
          jokersUsed={jokersUsed}
          groupJokersUsed={groupJokersUsed}
          saving={saving}
          globalLocked={globalLocked}
          user={user}
          api={api}
          activeLeague={null}
        />
      )}
    </div>
  );
}
